<template>
  <div v-if="!permissionDenied" class="indique-e-ganhe q-pa-md">
    <div class="text-h6 q-mb-sm">Indique e ganhe</div>

    <div
      v-if="loading"
      data-test="radio-affiliate-loading"
      class="text-body2 text-grey-7"
    >
      Carregando programa de indicação...
    </div>

    <div
      v-else-if="loadError"
      data-test="radio-affiliate-error"
      class="column q-gutter-sm"
    >
      <div class="text-body2 text-negative">
        {{ loadError }}
      </div>
      <q-btn
        data-test="radio-affiliate-retry"
        color="primary"
        label="Tentar novamente"
        no-caps
        @click="fetchAffiliate"
      />
    </div>

    <div
      v-else-if="affiliate"
      class="column q-gutter-sm"
    >
      <div class="indique-e-ganhe__code-line">
        <span class="text-body2 text-grey-7">Seu código</span>
        <q-chip
          data-test="radio-affiliate-code"
          color="primary"
          text-color="white"
          class="indique-e-ganhe__code"
        >
          {{ affiliate.affiliate_code }}
        </q-chip>
      </div>

      <q-input
        data-test="radio-affiliate-link"
        :model-value="affiliate.link"
        readonly
        type="textarea"
        autogrow
        class="indique-e-ganhe__link"
      >
        <template v-slot:append>
          <q-btn
            data-test="radio-affiliate-copy"
            color="primary"
            label="Copiar"
            no-caps
            aria-label="Copiar link de indicação"
            @click="copiarLink"
          />
        </template>
      </q-input>

      <div class="indique-e-ganhe__progress">
        <q-linear-progress
          data-test="radio-affiliate-progress"
          :value="progressoValue"
          color="primary"
          rounded
          size="12px"
        />
        <div
          data-test="radio-affiliate-progress-text"
          class="text-body2 text-grey-7"
        >
          {{ progressoTexto }}
        </div>
      </div>

      <div class="indique-e-ganhe__stats">
        <span
          data-test="radio-affiliate-meses"
          class="text-body2 text-grey-9"
        >
          {{ mesesGanhosTexto }}
        </span>
        <span class="text-grey-5">•</span>
        <span
          data-test="radio-affiliate-validadas"
          class="text-body2 text-grey-9"
        >
          {{ indicacoesPagasTexto }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios-auth";
import { copyToClipboard } from "quasar";

const INDICACOES_POR_BONUS = 3;

export default {
  name: "indique-e-ganhe",

  emits: ["gated"],
  data() {
    return {
      affiliate: null,
      loading: false,
      loadError: "",
      permissionDenied: false
    };
  },
  computed: {
    progressoFeitas() {
      if (!this.affiliate) {
        return 0;
      }

      const ateProximo = Number(this.affiliate.until_next_bonus);
      const restante = isNaN(ateProximo) ? INDICACOES_POR_BONUS : ateProximo;
      const feitas = INDICACOES_POR_BONUS - restante;

      return feitas < 0 ? 0 : feitas;
    },
    progressoValue() {
      return Math.max(0, Math.min(1, this.progressoFeitas / INDICACOES_POR_BONUS));
    },
    progressoTexto() {
      return this.progressoFeitas + " de " + INDICACOES_POR_BONUS + " indicações para o próximo bônus";
    },
    mesesGanhosTexto() {
      const meses = this.affiliate ? Number(this.affiliate.bonus_applied_total) : 0;
      const valor = isNaN(meses) ? 0 : meses;

      if (valor === 1) {
        return "1 mês ganho";
      }

      return valor + " meses ganhos";
    },
    indicacoesPagasTexto() {
      const pagas = this.affiliate ? Number(this.affiliate.total_validated) : 0;
      const valor = isNaN(pagas) ? 0 : pagas;

      if (valor === 1) {
        return "1 indicação paga";
      }

      return valor + " indicações pagas";
    }
  },
  mounted() {
    this.fetchAffiliate();
  },
  methods: {
    fetchAffiliate() {
      this.loading = true;
      this.loadError = "";
      this.permissionDenied = false;

      return axios.get("/radio/affiliate/me")
        .then(res => {
          this.loading = false;

          if (res && res.error) {
            this.handleError(res.error);
            return;
          }

          this.affiliate = res && res.data ? res.data : res;
        })
        .catch(error => {
          this.loading = false;
          this.handleError(error);
        });
    },
    handleError(error) {
      const status = error && error.response ? error.response.status : null;

      this.affiliate = null;

      if (status === 401 || status === 403) {
        this.permissionDenied = true;
        this.$emit("gated");
        return;
      }

      this.loadError = "Não foi possível carregar o programa de indicação. Verifique a conexão e tente novamente.";
    },
    copiarLink() {
      if (!this.affiliate || !this.affiliate.link) {
        return Promise.resolve();
      }

      return copyToClipboard(this.affiliate.link)
        .then(() => {
          this.$q.notify({
            message: "Link copiado",
            color: "green",
            position: "top-right"
          });
        })
        .catch(() => {
          this.$q.notify({
            message: "Não foi possível copiar o link",
            color: "red",
            position: "top-right"
          });
        });
    }
  }
};
</script>

<style scoped>
.indique-e-ganhe {
  border-top: 1px solid #e0e0e0;
}

.indique-e-ganhe__code-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.indique-e-ganhe__code {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.indique-e-ganhe__link {
  width: 100%;
}

.indique-e-ganhe__progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.indique-e-ganhe__stats {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
